import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Microphone, ArrowsClockwise, X } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { getProjects } from "../../api/projects";

function formatTimer(totalSeconds) {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const s = Math.floor(totalSeconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

/**
 * RecordingModal
 * Props:
 *   onClose()                    — dismiss without recording
 *   onRecordingStopped(payload)  — { projectId, projectName, blob, durationSeconds }
 */
export default function RecordingModal({ onClose, onRecordingStopped }) {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [phase, setPhase] = useState("select"); // select | requesting | recording
  const [elapsed, setElapsed] = useState(0);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);

  useEffect(() => {
    getProjects()
      .then((data) => setProjects((data || [])))
      .catch(() => toast.error("Could not load projects."))
      .finally(() => setLoadingProjects(false));
  }, []);

  const handleBeforeUnload = useCallback((e) => {
    e.preventDefault();
    e.returnValue = "";
    return "";
  }, []);

  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };

  // Safety: if the user closes the modal mid-recording via the X button.
  useEffect(() => () => cleanupStream(), []);

  const handleBeginRecording = async () => {
    if (!selectedProjectId) return;
    setPhase("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorderRef.current = recorder;
      recorder.start();

      startedAtRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 1000);

      window.addEventListener("beforeunload", handleBeforeUnload);
      setPhase("recording");
    } catch (e) {
      toast.error("Microphone access was denied or is unavailable.");
      setPhase("select");
    }
  };

  const handleStopAndSave = () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    const durationSeconds = elapsed;
    const project = projects.find((p) => p.id === selectedProjectId);

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      cleanupStream();
      onRecordingStopped({
        projectId: selectedProjectId,
        projectName: project?.name || "",
        blob,
        durationSeconds,
      });
    };
    recorder.stop();
  };

  const handleClose = () => {
    if (phase === "recording") {
      recorderRef.current?.stop();
    }
    cleanupStream();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="recording-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={handleClose} />
      <div className="relative z-10 bg-white border border-slate-200 w-full max-w-md shadow-xl rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-cabinet text-base font-bold text-slate-900 tracking-tight">
            Record Meeting
          </h2>
          <button onClick={handleClose} data-testid="close-recording-modal-btn" className="text-slate-400 hover:text-slate-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          {phase !== "recording" ? (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Project
                </label>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId} disabled={loadingProjects || phase === "requesting"}>
                  <SelectTrigger data-testid="record-project-select">
                    <SelectValue placeholder={loadingProjects ? "Loading projects..." : "Select a project..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`record-project-option-${p.name}`}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!loadingProjects && projects.length === 0 && (
                  <p className="text-xs text-slate-400">No active projects found. Create a project first.</p>
                )}
              </div>

              <Button
                onClick={handleBeginRecording}
                disabled={!selectedProjectId || phase === "requesting"}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="begin-recording-btn"
              >
                {phase === "requesting" ? (
                  <ArrowsClockwise size={14} className="mr-2 animate-spin" />
                ) : (
                  <Microphone size={14} className="mr-2" weight="fill" />
                )}
                {phase === "requesting" ? "Requesting microphone..." : "Begin Recording"}
              </Button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-4 py-4" data-testid="recording-active-state">
              <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-red-50 border-2 border-red-200">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-20 animate-ping" />
                <Microphone size={26} weight="fill" className="text-red-500 relative" />
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold tabular-nums text-slate-900" data-testid="recording-timer">
                  {formatTimer(elapsed)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Recording in progress...</p>
              </div>
              <Button
                onClick={handleStopAndSave}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="stop-recording-btn"
              >
                Stop & Save Recording
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
